import { useEffect, useState } from "react";
import type { ClientAccessProject } from "@potok/shared";
import type { ThemeId } from "@potok/ui";
import { THEME_IDS } from "@potok/ui";
import { httpPublicCatalogApi, type PublicCatalogApi } from "./api/catalog.api";
import { loadPublicTheme } from "./api/public-theme";
import { ClientCabinetPage } from "./features/cabinet/ClientCabinetPage";
import { readClientSession } from "./features/cabinet/client-session";
import { RegistrationPage } from "./features/registration/RegistrationPage";
import { ClientProjectView } from "./features/wizard/ClientProjectView";
import { Wizard } from "./features/wizard/Wizard";
import { AppShell, viewFromHash, type AppView } from "./layouts/AppShell";

type AppProps = {
  /** Клиент публичного каталога; подменяется в тестах. */
  catalogApi?: PublicCatalogApi;
  /** Загрузка темы; подменяется в тестах. */
  loadTheme?: () => Promise<{ webThemeId: ThemeId }>;
};

/**
 * Корневой экран клиентского SPA: wizard / регистрация / кабинет.
 */
export function App({
  catalogApi = httpPublicCatalogApi,
  loadTheme = loadPublicTheme,
}: AppProps = {}) {
  const [view, setView] = useState<AppView>(() =>
    typeof window === "undefined" ? "wizard" : viewFromHash(window.location.hash),
  );
  const [selectedProject, setSelectedProject] = useState<ClientAccessProject | null>(null);
  const [restoredProject, setRestoredProject] = useState<ClientAccessProject | null>(null);
  const [themeId, setThemeId] = useState<ThemeId>(THEME_IDS.DEFAULT);
  const [hasClientSession, setHasClientSession] = useState(() => readClientSession() !== null);

  useEffect(() => {
    let cancelled = false;
    loadTheme()
      .then((settings) => {
        if (!cancelled) {
          setThemeId(settings.webThemeId);
        }
      })
      .catch(() => {
        /* оставляем default */
      });
    return () => {
      cancelled = true;
    };
  }, [loadTheme]);

  useEffect(() => {
    function onHashChange(): void {
      setView(viewFromHash(window.location.hash));
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  function goWizard(): void {
    window.location.hash = "";
    setView("wizard");
  }

  function goCabinet(): void {
    window.location.hash = "client-access";
    setView("cabinet");
    setHasClientSession(readClientSession() !== null);
  }

  function goRegister(): void {
    window.location.hash = "register";
    setView("register");
  }

  const showMinimalShell = view === "cabinet" && !hasClientSession;

  return (
    <AppShell
      themeId={themeId}
      activeView={view}
      minimal={showMinimalShell}
      onNavigate={(next) => {
        setView(next);
        setHasClientSession(readClientSession() !== null);
      }}
      hasClientSession={hasClientSession}
    >
      {view === "register" ? (
        <RegistrationPage
          api={catalogApi}
          onGoWizard={goWizard}
          onGoCabinet={goCabinet}
          onSessionSaved={() => setHasClientSession(true)}
        />
      ) : view === "cabinet" ? (
        <>
          <ClientCabinetPage
            api={catalogApi}
            onOpenProject={setSelectedProject}
            onNewProject={goWizard}
            onGoRegister={goRegister}
            onLogout={() => setHasClientSession(false)}
          />
          {selectedProject ? (
            <ClientProjectView
              api={catalogApi}
              project={selectedProject}
              onRestore={(project) => {
                setRestoredProject(project);
                setSelectedProject(null);
                goWizard();
              }}
              onClose={() => setSelectedProject(null)}
            />
          ) : null}
        </>
      ) : (
        <Wizard catalogApi={catalogApi} restoredProject={restoredProject} />
      )}
    </AppShell>
  );
}
